import { PageLayout } from '../components/PageLayout';
import { Globe, Shield, Lock, Server, Check, MapPin, ArrowRight } from 'lucide-react';

/**
 * Sovereign AI Page
 *
 * Targeting: National AI labs, sovereign wealth fund-backed compute providers, government contractors.
 * Message: Your sovereign AI cloud deserves sovereign AI intelligence.
 */
export const Sovereign = () => {
    return (
        <PageLayout>
            <section className="py-24 lg:py-32 bg-obsidian-900">
                <div className="max-w-7xl mx-auto px-6">
                    {/* Hero */}
                    <div className="text-center mb-20">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-volt-500/10 border border-volt-500/20 text-volt-400 text-sm font-medium mb-6">
                            <Globe className="w-4 h-4" />
                            Sovereign AI
                        </div>
                        <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
                            Your sovereign AI cloud
                            <br />
                            <span className="text-gray-500">deserves sovereign AI intelligence.</span>
                        </h1>
                        <p className="text-xl text-gray-400 max-w-3xl mx-auto">
                            Nations are investing in domestic AI infrastructure like electricity or the internet.
                            Forecaster ensures every token stays within your borders — enforced per request, not per cluster.
                        </p>
                    </div>

                    {/* The Problem */}
                    <div className="mb-20">
                        <h2 className="text-2xl font-bold text-white mb-8 text-center">Why Sovereign AI Needs an Intelligence Layer</h2>
                        <div className="grid md:grid-cols-3 gap-6">
                            <div className="p-6 rounded-xl bg-white/[0.02] border border-white/[0.06]">
                                <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center mb-4">
                                    <MapPin className="w-5 h-5 text-red-400" />
                                </div>
                                <h3 className="text-lg font-semibold text-white mb-2">Data Leaves the Country</h3>
                                <p className="text-sm text-gray-400">
                                    Without per-request routing enforcement, inference requests can leak to foreign regions
                                    during overflow events. Cluster-level policies aren't enough.
                                </p>
                            </div>
                            <div className="p-6 rounded-xl bg-white/[0.02] border border-white/[0.06]">
                                <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center mb-4">
                                    <Server className="w-5 h-5 text-red-400" />
                                </div>
                                <h3 className="text-lg font-semibold text-white mb-2">Domestic GPUs Sit Idle</h3>
                                <p className="text-sm text-gray-400">
                                    Nations invest billions in GPU clusters but lack the intelligence to keep them productive.
                                    Average utilization: 15-30%. That's 70% of sovereign investment wasted.
                                </p>
                            </div>
                            <div className="p-6 rounded-xl bg-white/[0.02] border border-white/[0.06]">
                                <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center mb-4">
                                    <Lock className="w-5 h-5 text-red-400" />
                                </div>
                                <h3 className="text-lg font-semibold text-white mb-2">Compliance Is Manual</h3>
                                <p className="text-sm text-gray-400">
                                    SOC2, ISO27001, HIPAA, GDPR — each regulation needs enforcement at the infrastructure level.
                                    Manual policy enforcement doesn't scale with token volume.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* How Forecaster Solves This */}
                    <div className="mb-20">
                        <h2 className="text-2xl font-bold text-white mb-8 text-center">Per-Request Sovereignty Enforcement</h2>
                        <div className="bg-obsidian-800/50 border border-white/[0.06] rounded-2xl p-8">
                            <div className="grid lg:grid-cols-2 gap-8">
                                {/* Left: Compliance zones */}
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-4">Compliance Zones</h3>
                                    <p className="text-sm text-gray-400 mb-6">
                                        Declare zones. Forecaster enforces them on every request. No exceptions during overflow,
                                        no leaks during traffic spikes, no manual intervention.
                                    </p>
                                    <div className="space-y-3">
                                        {[
                                            { zone: 'EU (GDPR)', detail: 'Data never leaves EU member states' },
                                            { zone: 'US (SOC2)', detail: 'FedRAMP-aligned region restrictions' },
                                            { zone: 'HIPAA', detail: 'Healthcare data in certified regions only' },
                                            { zone: 'Custom', detail: 'Define your own zones per regulation' },
                                        ].map((z) => (
                                            <div key={z.zone} className="flex items-start gap-3 p-3 rounded-lg bg-obsidian-900/60">
                                                <Check className="w-4 h-4 text-volt-400 mt-0.5 flex-shrink-0" />
                                                <div>
                                                    <span className="text-sm font-medium text-white">{z.zone}</span>
                                                    <p className="text-xs text-gray-500">{z.detail}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Right: What gets enforced */}
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-4">Enforced Per Request</h3>
                                    <p className="text-sm text-gray-400 mb-6">
                                        Not cluster-level. Not namespace-level. Every single inference request is checked
                                        against compliance zones before routing.
                                    </p>
                                    <div className="space-y-3">
                                        {[
                                            { label: 'Region filtering', desc: 'Excluded regions never receive traffic' },
                                            { label: 'Model locality', desc: 'Weights stay within declared zones' },
                                            { label: 'KV cache isolation', desc: 'Session data never crosses zone boundaries' },
                                            { label: 'Overflow containment', desc: 'Multi-cloud overflow respects zones — no leaks' },
                                            { label: 'Audit trail', desc: 'Per-request logging of routing decisions and zone enforcement' },
                                            { label: 'Multi-tenancy', desc: 'Tenant isolation across shared GPU infrastructure' },
                                        ].map((item) => (
                                            <div key={item.label} className="flex items-start gap-3 p-3 rounded-lg bg-obsidian-900/60">
                                                <Shield className="w-4 h-4 text-volt-400 mt-0.5 flex-shrink-0" />
                                                <div>
                                                    <span className="text-sm font-medium text-white">{item.label}</span>
                                                    <p className="text-xs text-gray-500">{item.desc}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* CapEx Efficiency */}
                    <div className="mb-20">
                        <h2 className="text-2xl font-bold text-white mb-8 text-center">Make Sovereign CapEx Productive</h2>
                        <div className="grid md:grid-cols-3 gap-6 mb-8">
                            <div className="p-6 rounded-xl border border-volt-500/20 bg-volt-500/[0.03] text-center">
                                <p className="text-4xl font-bold text-volt-400 mb-2">30% → 87%</p>
                                <p className="text-sm text-gray-400">GPU utilization</p>
                                <p className="text-xs text-gray-600 mt-1">Same GPUs, 3x the productive compute</p>
                            </div>
                            <div className="p-6 rounded-xl border border-volt-500/20 bg-volt-500/[0.03] text-center">
                                <p className="text-4xl font-bold text-volt-400 mb-2">100%</p>
                                <p className="text-sm text-gray-400">Self-hosted</p>
                                <p className="text-xs text-gray-600 mt-1">No external dependencies. Your cluster, your data.</p>
                            </div>
                            <div className="p-6 rounded-xl border border-volt-500/20 bg-volt-500/[0.03] text-center">
                                <p className="text-4xl font-bold text-volt-400 mb-2">847</p>
                                <p className="text-sm text-gray-400">Tokens per Joule</p>
                                <p className="text-xs text-gray-600 mt-1">Revenue per megawatt, measured per request</p>
                            </div>
                        </div>
                        <div className="p-6 rounded-xl bg-gradient-to-r from-volt-500/10 via-volt-500/5 to-transparent border border-volt-500/20 text-center">
                            <p className="text-lg text-white font-medium mb-2">
                                Same CapEx. 3x the revenue.
                            </p>
                            <p className="text-gray-400 max-w-2xl mx-auto">
                                Your $100M GPU investment generates $X in token revenue at 30% utilization.
                                At 87% utilization, it generates $3X. Forecaster is the difference.
                            </p>
                        </div>
                    </div>

                    {/* CTA */}
                    <div className="text-center">
                        <a
                            href="/trial"
                            className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-volt-500 text-obsidian-900 rounded-lg font-semibold text-lg hover:bg-volt-400 transition-all hover:shadow-volt group"
                        >
                            Start Free Trial
                            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </a>
                        <p className="text-gray-600 text-sm mt-4">
                            One Helm install. Self-hosted. Data never leaves your cluster.
                        </p>
                    </div>
                </div>
            </section>
        </PageLayout>
    );
};
